import { FunnelStatus } from '@/__generated__/graphql';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { SORT_ORDER } from '@/components/table/types';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { CREATE_DUE_DILIGENCE_FUNNEL_MUTATION, DUE_DILIGENCE_FUNNEL_LIST_SETTINGS_QUERY, GET_FUNNEL_QUERY } from '../queries';
import { IFunnelDataWithStatuses } from '../types';
import { formatTiersData } from '../utils';

interface CreateNewVersionProps {
  onClose: () => void;
  isOpen: boolean;
  openFunnelEditor: (funnelId: string, funnelName: string, isViewMode?: boolean, isDraft?: boolean) => void;
  currentFunnel: IFunnelDataWithStatuses | null;
}

const CreateNewVersion = ({ onClose, isOpen, openFunnelEditor, currentFunnel }: CreateNewVersionProps) => {
  if (!currentFunnel) return <></>;

  const { data } = useQuery(GET_FUNNEL_QUERY, {
    variables: {
      id: currentFunnel.id
    },
    skip: !currentFunnel.id
  });

  const [createFunnel] = useMutation(CREATE_DUE_DILIGENCE_FUNNEL_MUTATION, {
    refetchQueries: () => [
      {
        query: DUE_DILIGENCE_FUNNEL_LIST_SETTINGS_QUERY,
        variables: {
          data: {
            statusFilter: ['PUBLISHED', 'DRAFT_WITHOUT_PUBLISHED'],
            sortDirection: SORT_ORDER.ASC,
            sortBy: 'ORDER'
          },
          archivedFunnelData: {
            sortDirection: SORT_ORDER.ASC,
            sortBy: 'ORDER'
          }
        }
      }
    ]
  });

  const saveValue = () => {
    if (data) {
      const { tiers } = data.getDueDiligenceFunnel;

      const updatedTiers = formatTiersData(tiers);

      createFunnel({
        variables: {
          data: {
            name: currentFunnel.name,
            funnelStaticId: currentFunnel.funnelStaticId,
            externalId: 'test-3',
            status: FunnelStatus.Draft,
            tiers: updatedTiers
          }
        },
        onCompleted: (data) => {
          const { id, name } = data.createDueDiligenceFunnel;
          openFunnelEditor(id, name, false, true);
        }
      });
      onClose();
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Draft Version"
      confirmButton={{ name: 'Confirm', onClick: saveValue, disabled: !data }}
    >
      <ModalDescription>
        You cannot edit a published funnel directly. This action will create a new draft version that you can modify. Once finished, you can publish
        it to replace the current version. Note that you cannot modify the tiers for an existing funnel, only the sections, fields, and checklists
        within each tier.
      </ModalDescription>
    </ModalWrappedContent>
  );
};

export default CreateNewVersion;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 27px;
  text-align: center;
  color: ${themeVar('font.base')};
  font-weight: 400;
`;
